import { useState } from "react";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { useAuth } from "react-oidc-context";
import { ArrowDown, ArrowUp, Plus, Trash2, Upload } from "lucide-react";

import {
  RULE_CLASSES,
  getAudienceClassification,
  getAudienceClassificationHistory,
  getAudienceClassificationVersion,
  putAudienceClassification,
  type AudienceClassification,
  type AudienceClassificationVersion,
  type ClassificationRule,
  type DeviceClass,
} from "@/api/audienceClassification";
import { ApiError } from "@/api/client";
import { Field, Input, Select, Textarea } from "@/components/form";
import { MutationError, RefreshFailed, loadFailure } from "@/components/Spinner";
import { Button, EmptyState, IconButton, Page, PageHeader, Table, type TableColumn } from "@/components/ui";
import { useRequestLeave, useUnsavedChanges } from "@/hooks/useUnsavedChanges";
import { formatDateTime } from "@/lib/dates";

const CLASS_LABELS: Record<DeviceClass, string> = {
  robot: "Robot",
  smartphone: "Smartphone",
  feature_phone: "Feature phone",
  tablet: "Tablet",
  desktop: "Desktop",
  smart_speaker: "Smart speaker",
  car: "Car",
  console: "Console",
  wearable: "Wearable",
  tv: "TV",
  portable_media_player: "Portable media player",
  physical_media_player: "Physical media player",
  camera: "Camera",
};
const CLASS_OPTIONS = RULE_CLASSES.map((value) => ({ value, label: CLASS_LABELS[value] }));
const FILTER_OPTIONS = [{ value: "", label: "All classes" }, ...CLASS_OPTIONS];
const KIND_OPTIONS = [
  { value: "substring", label: "substring" },
  { value: "regex", label: "regex" },
];

/** A row being edited: a rule plus a local identity, so that removing or moving a row does not shift the focus of the others. */
interface Row {
  id: number;
  kind: ClassificationRule["kind"];
  value: string;
  class: DeviceClass;
  note: string;
}

let nextRowId = 0;
const newRow = (rule: Partial<Row> = {}): Row => ({
  id: nextRowId++,
  kind: rule.kind ?? "substring",
  value: rule.value ?? "",
  class: rule.class ?? "smartphone",
  note: rule.note ?? "",
});

const toRows = (rules: ClassificationRule[]): Row[] => rules.map((r) => newRow({ ...r, note: r.note ?? "" }));
const toRules = (rows: Row[]): ClassificationRule[] =>
  rows.map((r) =>
    r.note
      ? { kind: r.kind, value: r.value, class: r.class, note: r.note }
      : { kind: r.kind, value: r.value, class: r.class },
  );

/** The rules as the server holds them, in the form `toRules` builds (key order and extra keys of the reply do not count). */
const canonical = (rules: ClassificationRule[]): ClassificationRule[] => toRules(toRows(rules));

const REGEX_PREFIX = "regex:";

/** `regex:<motif>` is a regex, any other line a substring; blank lines are dropped. */
function parseImport(text: string): Pick<Row, "kind" | "value">[] {
  return text
    .split(/\r?\n/)
    .map((line) => line.trim())
    .filter((line) => line !== "")
    .map((line) =>
      line.startsWith(REGEX_PREFIX)
        ? { kind: "regex" as const, value: line.slice(REGEX_PREFIX.length) }
        : { kind: "substring" as const, value: line },
    )
    .filter((entry) => entry.value !== "");
}

const countRules = (rules: { class: DeviceClass }[]) => {
  const robots = rules.filter((r) => r.class === "robot").length;
  return `${robots} robot rules · ${rules.length - robots} device rules`;
};

/** The 0-based index of the rule a 400 refuses (`details.index`), or `undefined` for any other failure. */
function refusedIndex(error: unknown): number | undefined {
  if (!(error instanceof ApiError) || error.status !== 400) return undefined;
  const index = (error.body as { details?: { index?: unknown } } | null)?.details?.index;
  return typeof index === "number" ? index : undefined;
}

function Editor({ list, token }: { list: AudienceClassification; token: string }) {
  const qc = useQueryClient();
  const [rows, setRows] = useState<Row[]>(() => toRows(list.rules));
  const [classFilter, setClassFilter] = useState("");
  const [search, setSearch] = useState("");

  const [importOpen, setImportOpen] = useState(false);
  const [importText, setImportText] = useState("");
  const [importClass, setImportClass] = useState<DeviceClass>("robot");
  // The row a 400 refused, by identity: removing or moving a row above it must not move the mark.
  const [refusedId, setRefusedId] = useState<number | undefined>(undefined);

  const saveMut = useMutation({
    // The rows are the ones SENT: the index a 400 names refers to them, not to what the operator edits meanwhile.
    mutationFn: (sent: Row[]) => putAudienceClassification(token, list.version, toRules(sent)),
    onError: (error, sent) => {
      const index = refusedIndex(error);
      if (index === undefined) return;
      setRefusedId(sent[index]?.id);
      // A refused row hidden by the filter or the search would be an error nobody can see.
      setClassFilter("");
      setSearch("");
    },
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ["audience-classification"] });
    },
  });

  const dirty = JSON.stringify(toRules(rows)) !== JSON.stringify(canonical(list.rules));

  useUnsavedChanges(dirty);
  const requestLeave = useRequestLeave();
  const stale = saveMut.error instanceof ApiError && saveMut.error.status === 412;
  // Reloading drops the edits: with some, the operator is asked first, as for any other way out of the page.
  const reload = () => requestLeave(() => qc.invalidateQueries({ queryKey: ["audience-classification"] }));

  const update = (id: number, patch: Partial<Row>) => {
    if (id === refusedId) setRefusedId(undefined);
    setRows((current) => current.map((r) => (r.id === id ? { ...r, ...patch } : r)));
  };
  const remove = (id: number) => {
    if (id === refusedId) setRefusedId(undefined);
    setRows((current) => current.filter((r) => r.id !== id));
  };
  const addImported = () => {
    setRows((current) => {
      const seen = new Set(current.map((r) => `${r.kind}\u0000${r.value}`));
      const added: Row[] = [];
      for (const entry of parseImport(importText)) {
        const key = `${entry.kind}\u0000${entry.value}`;
        if (seen.has(key)) continue;
        seen.add(key);
        added.push(newRow({ ...entry, class: importClass }));
      }
      return [...current, ...added];
    });
    setImportText("");
    setImportOpen(false);
  };
  const move = (id: number, by: -1 | 1) =>
    setRows((current) => {
      const from = current.findIndex((r) => r.id === id);
      const to = from + by;
      if (from < 0 || to < 0 || to >= current.length) return current;
      const next = [...current];
      [next[from], next[to]] = [next[to], next[from]];
      return next;
    });

  const needle = search.trim().toLowerCase();
  const filtering = classFilter !== "" || needle !== "";
  const visible = rows.filter(
    (r) =>
      (classFilter === "" || r.class === classFilter) &&
      (needle === "" || r.value.toLowerCase().includes(needle) || r.note.toLowerCase().includes(needle)),
  );

  // Row id -> position, computed once per render: the cells below read it instead of scanning the rows.
  const positions = new Map(rows.map((r, i) => [r.id, i]));
  const columns: TableColumn<Row>[] = [
    {
      key: "order",
      header: "Order",
      align: "right",
      mono: true,
      muted: true,
      cell: (row) => String((positions.get(row.id) ?? 0) + 1),
    },
    {
      key: "kind",
      header: "Kind",
      cell: (row) => (
        <Select
          aria-label="Kind"
          value={row.kind}
          options={KIND_OPTIONS}
          onChange={(e) => update(row.id, { kind: e.target.value as Row["kind"] })}
        />
      ),
    },
    {
      key: "value",
      header: "Value",
      wide: true,
      cell: (row) => (
        <Input
          aria-label="Value"
          className="font-mono"
          value={row.value}
          onChange={(e) => update(row.id, { value: e.target.value })}
        />
      ),
    },
    {
      key: "class",
      header: "Class",
      cell: (row) => (
        <Select
          aria-label="Class"
          value={row.class}
          options={CLASS_OPTIONS}
          onChange={(e) => update(row.id, { class: e.target.value as DeviceClass })}
        />
      ),
    },
    {
      key: "note",
      header: "Note",
      wide: true,
      cell: (row) => <Input aria-label="Note" value={row.note} onChange={(e) => update(row.id, { note: e.target.value })} />,
    },
    {
      key: "move",
      header: "Move",
      align: "right",
      nowrap: true,
      cell: (row) => {
        const position = positions.get(row.id) ?? 0;
        return (
          <span className="inline-flex gap-1">
            <IconButton
              icon={ArrowUp}
              label={`Move ${row.value || "empty rule"} up`}
              disabled={filtering || position === 0}
              onClick={() => move(row.id, -1)}
            />
            <IconButton
              icon={ArrowDown}
              label={`Move ${row.value || "empty rule"} down`}
              disabled={filtering || position === rows.length - 1}
              onClick={() => move(row.id, 1)}
            />
          </span>
        );
      },
    },
    {
      key: "remove",
      header: "Remove",
      align: "right",
      cell: (row) => (
        <IconButton
          icon={Trash2}
          label={`Remove ${row.value || "empty rule"}`}
          onClick={() => remove(row.id)}
        />
      ),
    },
  ];

  return (
    <div className="space-y-4">
      <div className="flex flex-wrap items-center gap-2">
        <Button icon={Plus} onClick={() => setRows((current) => [...current, newRow()])}>
          Add rule
        </Button>
        <Button icon={Upload} aria-expanded={importOpen} onClick={() => setImportOpen((open) => !open)}>
          Import
        </Button>
        <Select aria-label="Filter by class" value={classFilter} options={FILTER_OPTIONS} onChange={(e) => setClassFilter(e.target.value)} />
        <Input aria-label="Search rules" placeholder="Search value or note" value={search} onChange={(e) => setSearch(e.target.value)} />
        <span className="text-sm text-ink-muted">{countRules(rows)}</span>
        <Button
          variant="primary"
          className="ml-auto"
          disabled={!dirty}
          loading={saveMut.isPending}
          onClick={() => {
            setRefusedId(undefined);
            saveMut.mutate(rows);
          }}
        >
          Save
        </Button>
      </div>

      {importOpen && (
        <div className="space-y-2">
          <Field label="One rule per line" help="Prefix a line with regex: for a regular expression; any other line is a substring. All lines get the class below, and are added at the end of the list.">
            <Textarea mono rows={6} value={importText} onChange={(e) => setImportText(e.target.value)} />
          </Field>
          <Field label="Class of the imported rules">
            <Select value={importClass} options={CLASS_OPTIONS} onChange={(e) => setImportClass(e.target.value as DeviceClass)} />
          </Field>
          <Button variant="primary" disabled={importText.trim() === ""} onClick={addImported}>
            Add to list
          </Button>
        </div>
      )}

      <MutationError mutation={saveMut} title="Could not save the rules" />
      {stale && <Button onClick={reload}>Reload latest version</Button>}

      <Table
        label="Classification rules"
        columns={columns}
        rows={visible}
        rowKey={(row) => String(row.id)}
        rowEmphasis={(row) => (row.id === refusedId ? "danger" : undefined)}
        empty={
          filtering ? (
            <EmptyState title="No rule matches">Change the class filter or the search to see other rules.</EmptyState>
          ) : (
            <EmptyState title="No rules yet">Add a rule, then save it.</EmptyState>
          )
        }
      />
    </div>
  );
}

function History({ token }: { token: string }) {
  const [viewing, setViewing] = useState<number | null>(null);
  const query = useQuery({
    queryKey: ["audience-classification", "history"],
    queryFn: () => getAudienceClassificationHistory(token),
    enabled: !!token,
  });
  const version = useQuery({
    queryKey: ["audience-classification", "version", viewing],
    queryFn: () => getAudienceClassificationVersion(token, viewing ?? 0),
    enabled: !!token && viewing !== null,
  });
  const columns: TableColumn<AudienceClassificationVersion>[] = [
    { key: "version", header: "Version", mono: true, cell: (item) => String(item.version) },
    { key: "author", header: "Author", cell: (item) => item.updatedBy || "—" },
    { key: "date", header: "Date", mono: true, muted: true, nowrap: true, cell: (item) => formatDateTime(item.updatedAt) },
    { key: "count", header: "Rules", align: "right", mono: true, cell: (item) => String(item.ruleCount) },
    {
      key: "view",
      header: "Rules of the version",
      align: "right",
      cell: (item) => (
        <Button
          size="sm"
          variant="ghost"
          aria-label={`View version ${item.version}`}
          aria-pressed={viewing === item.version}
          onClick={() => setViewing(item.version)}
        >
          View
        </Button>
      ),
    },
  ];
  const ruleColumns: TableColumn<ClassificationRule>[] = [
    { key: "kind", header: "Kind", cell: (rule) => rule.kind },
    { key: "value", header: "Value", mono: true, wide: true, cell: (rule) => rule.value },
    { key: "class", header: "Class", cell: (rule) => CLASS_LABELS[rule.class] ?? rule.class },
    { key: "note", header: "Note", wide: true, cell: (rule) => rule.note || "—" },
  ];
  return (
    <section className="space-y-4">
      <h2 className="text-sm font-semibold text-ink">History</h2>
      <RefreshFailed query={query} />
      <Table
        label="History"
        columns={columns}
        rows={query.data?.items}
        rowKey={(item) => String(item.version)}
        error={loadFailure(query)}
        empty={<EmptyState title="No history yet">Each saved version of the rules appears here.</EmptyState>}
      />
      {viewing !== null && (
        <div className="space-y-2">
          <h3 className="text-sm font-semibold text-ink">Version {viewing} (read-only)</h3>
          <Table
            label={`Rules of version ${viewing}`}
            columns={ruleColumns}
            rows={version.data?.rules}
            rowKey={(_, index) => String(index)}
            error={loadFailure(version)}
            empty={<EmptyState title="No rules in this version">This version was saved without any rule.</EmptyState>}
          />
        </div>
      )}
    </section>
  );
}

export function AudienceClassificationPage() {
  const auth = useAuth();
  const token = auth.user?.id_token ?? "";

  const query = useQuery({
    queryKey: ["audience-classification"],
    queryFn: () => getAudienceClassification(token),
    enabled: !!token,
  });
  const list = query.data;

  return (
    <Page>
      <PageHeader
        title="Audience classification"
        subtitle={
          <>
            Robots are excluded from measurement wherever they appear in the list. Device rules are tried top to bottom; the first match sets
            the class.
            {list && (
              <span className="mt-1 block text-ink-muted">
                Version {list.version}
                {list.updatedBy ? ` · by ${list.updatedBy}` : ""}
                {list.updatedAt > 0 ? ` · ${formatDateTime(list.updatedAt)}` : ""}
              </span>
            )}
          </>
        }
      />
      <div className="space-y-8">
        <RefreshFailed query={query} />
        {list ? (
          // Keyed by version: rules saved or reloaded start the editor over from what the server holds.
          <Editor key={list.version} list={list} token={token} />
        ) : (
          <Table
            label="Classification rules"
            columns={[{ key: "value", header: "Value", cell: () => null }]}
            rows={undefined}
            rowKey={() => ""}
            error={loadFailure(query)}
            empty={null}
          />
        )}
        <History token={token} />
      </div>
    </Page>
  );
}
